import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useInterest, likeInterest } from "../lib/interest";
import { SITE } from "../config/site";

export const InterestButton = ({ onApply, testid = "interest-btn", quiet = false }) => {
  const { total, clicked, ready } = useInterest();
  const showCount = ready && total >= SITE.interestDisplayThreshold;

  const handleClick = async () => {
    if (clicked) {
      toast("Already noted 🖤 — thanks for the love.", {
        description: "Want your own drop?",
        action: { label: "Become a founding artist →", onClick: () => onApply?.() },
      });
      return;
    }
    const res = await likeInterest();
    if (res.error) {
      toast.error("Couldn't register that just now. Please try again.");
      return;
    }
    toast.success("Thank you 🖤 — you're one of the artists shaping VYNLWORLD.", {
      description: "Want your own drop?",
      action: { label: "Become a founding artist →", onClick: () => onApply?.() },
    });
  };

  return (
    <button
      data-testid={testid}
      onClick={handleClick}
      aria-pressed={clicked}
      aria-label={clicked ? "You've shown interest" : "Show your interest"}
      className={
        quiet
          ? "group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-light transition-colors duration-300"
          : "btn-sharp group inline-flex items-center gap-3 px-5 py-4 text-xs uppercase tracking-[0.22em] font-medium"
      }
      style={
        quiet
          ? { backgroundColor: "transparent", color: clicked ? "#F4EEF7" : "#9B8FAC" }
          : {
              backgroundColor: clicked ? "rgba(199,36,177,0.12)" : "transparent",
              border: "1px solid #2A2233",
              color: "#F4EEF7",
            }
      }
      onMouseEnter={(e) => {
        if (quiet) e.currentTarget.style.color = "#F4EEF7";
        else e.currentTarget.style.borderColor = "#C724B1";
      }}
      onMouseLeave={(e) => {
        if (quiet) e.currentTarget.style.color = clicked ? "#F4EEF7" : "#9B8FAC";
        else e.currentTarget.style.borderColor = "#2A2233";
      }}
    >
      <motion.span
        key={clicked ? "on" : "off"}
        initial={clicked ? { scale: 0.6 } : false}
        animate={clicked ? { scale: [0.6, 1.35, 1] } : { scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="inline-flex"
      >
        <Heart
          size={quiet ? 15 : 18}
          strokeWidth={2}
          color={clicked ? "#C724B1" : quiet ? "#9B8FAC" : "#F4EEF7"}
          fill={clicked ? "#C724B1" : "none"}
          className="transition-colors duration-300 group-hover:opacity-90"
        />
      </motion.span>
      <span>{clicked ? "Thanks" : "Show interest"}</span>
      <AnimatePresence>
        {showCount && (
          <motion.span
            data-testid="interest-count"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            className="font-display text-sm tabular-nums"
            style={{ color: "#F0C420" }}
          >
            {total}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

export default InterestButton;
